import React, {
  MutableRefObject,
  useCallback,
  useEffect,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";

import { OrderPopupCard } from "./styled";
import { OrderModel } from "../../shared/models/order/order-item.model";
import { useAppDispatch, useAppSelector } from "../../../redux/store";
import { getOrdersStatesSummary } from "../../../redux/slices/orders-slice";
import { changeActionbarStatus } from "../../../redux/slices/actionbar.slice";
import { ActionbarStatus } from "../../../redux/reducers/actionbar-reducers";
import { OrderPopupCountries } from "./country-selector/country-selector";
import {
  clearNewOrder,
  createOrder,
  // newOrderSetProducts,
} from "../../../redux/slices/new-order-slice";
import { ShippingMethodSelector } from "./shipping-method-selector/shipping-method-selector";
import NewOrderDetails from "./order-details/new-order-details";
import BarekamText from "../../components/text";
import { Message, MessageSections } from "../../components/message/message";
import { ProductDetails } from "./products/product-details";
import ordersService, {
  // getEmptyProduct,
  validateOrder,
  validateProducts,
} from "../../../services/orders.service";
import { ActionBar } from "../../components/action-bar/action-bar";
import BarekamButton, { ActionbarButtons } from "../../components/button";
import { parseUpdateDataForAnnouncing } from "../../../management-pages/actions/dialogs/blocks/announce-order";
import LimitAvoidModal from "./limit-avoid";
import { getCurrentRate } from "../../../redux/slices/static-data.slice";

const NewOrderPopup = ({
  order,
  orderValidationErrors,
  productValidationErrors,
  showToast,
  mainContentContainerRef,
  setOrderValidationErrors,
  setProductValidationErrors,
}: NewOrderPopupProps) => {
  const [focusedProduct, setFocusedProduct] = useState(0);
  const [dialogOpen, setDialogState] = useState<boolean>(false);
  const { currentRate } = useAppSelector(
      (state) => state.staticDataReducer,
    );

  const { languageId } = useAppSelector((state) => state.messagesReducer);

  const { matchesPhone } = useAppSelector((state) => state.deviceReducer);
  const { recipient } = order;

  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  useEffect(() => {
    dispatch(getCurrentRate());
  }, [])

  // function onAddNewProduct() {
  //   const emptyProduct = getEmptyProduct(currencies, weightUnits);
  //   dispatch(
  //     newOrderSetProducts([...(order ? order.products : []), emptyProduct]),
  //   );
  //   setFocusedProduct(order?.products.length || 0);
  // }

  const addOrder = useCallback(() => {
    if (!order) return;

    const orderValidationErrors = validateOrder(order);
    const productValidationErrors = validateProducts(order);

    if (
      Object.keys(orderValidationErrors).length > 0 ||
      Object.keys(productValidationErrors).length > 0
    ) {
      setOrderValidationErrors(orderValidationErrors);
      setProductValidationErrors(productValidationErrors);
      return;
    }

    try {
        dispatch(createOrder({ order, languageId })).then(async (response) => {
          if (response.payload.status === 405) {
            const data = response.payload.data;
            if (data.trackingNumber) {
              if (data.trackingNumber === "product_exists") {
                setProductValidationErrors({
                  0: { tracking_number: "already_exists" },
                });
              } else if (data.trackingNumber === "unidentified_order_exists") {
                const updateData = parseUpdateDataForAnnouncing(order);
                await ordersService.announceOrder(data.orderId, updateData);
                dispatch(getOrdersStatesSummary());
                closePopup();
              }
            }
          } else {
            dispatch(getOrdersStatesSummary());
            closePopup();
          }
        });
    } catch (error) {
      console.error(error);
    }
  }, [order, languageId]);

  const buttonHandler = () => {
    if (!recipient?.monthlyLimit) return addOrder();

    const { priceLimitPerParcel, weightLimitPerParcel, currentMonthOrdersPriceSum } =
      recipient.monthlyLimit;
    const product = order?.products?.[0];
    const amdRate = Number(currentRate?.Rates?.ExchangeRate?.find(
      rate => rate?.ISO === product?.currency?.name[4],
    )?.Rate) || 1;

    const priceToAmd = product?.price && amdRate ? +product?.price * amdRate : product?.price;

    if (
      product && priceToAmd &&
      ((priceLimitPerParcel &&
        priceToAmd &&
        priceToAmd + Number(currentMonthOrdersPriceSum) >= priceLimitPerParcel) ||
        (weightLimitPerParcel &&
          product.weight &&
          product.weight >= weightLimitPerParcel))
    ) {
      setDialogState(true);
    } else {
     addOrder();
    }
  }

  function closePopup() {
    dispatch(clearNewOrder());
    dispatch(changeActionbarStatus(ActionbarStatus.DEFAULT));
    navigate("../my");
  }

  return (
    <>
      <ActionBar
        buttonText={
          <>
            <BarekamText variant={"m"} fontWeight={700}>
              <Message
                messageKey={"submit"}
                sectionId={MessageSections.ORDERS}
              />
            </BarekamText>
          </>
        }
        buttonFunction={buttonHandler}
        headerText={
          <Message
            messageKey={"new_order"}
            sectionId={MessageSections.ORDERS}
          />
        }
        mainContentContainerRef={mainContentContainerRef}
        sticky={!matchesPhone}
        withBackButton
      />

      <OrderPopupCard>
        <OrderPopupCountries shippingInfo={order.shippingInfo} />
      </OrderPopupCard>

      <OrderPopupCard>
        <ShippingMethodSelector shippingInfo={order.shippingInfo} withLink />
      </OrderPopupCard>

      <OrderPopupCard>
        <NewOrderDetails
          order={order}
          validationErrors={orderValidationErrors}
        />
      </OrderPopupCard>

      <div style={{ width: "100%", maxWidth: "43rem", padding: "1rem" }}>
        <BarekamText variant={"m"} bold>
          <Message
            messageKey={"order_products"}
            sectionId={MessageSections.ORDERS}
          />
        </BarekamText>
      </div>

      {order?.products.map((product, index) => (
        <ProductDetails
          order={order}
          product={product}
          products={order.products}
          allProductValidationErrors={productValidationErrors}
          setAllProductValidationErrors={setProductValidationErrors}
          isNewOrder={true}
          index={index}
          expandedByDefault={true}
          focused={index === focusedProduct}
          key={`${product.productId}-${index}`}
        />
      ))}

      {matchesPhone ? (
        <div
          style={{ width: "100%", paddingLeft: "1rem", paddingRight: "1rem" }}
        >
          <BarekamButton
            type={ActionbarButtons.NEW}
            sx={{ width: matchesPhone ? "100%" : "max-content" }}
            size={"m"}
            onClick={() => buttonHandler()}
          >
            <BarekamText variant={"m"} fontWeight={700}>
              <Message
                messageKey={"submit"}
                sectionId={MessageSections.ORDERS}
              />
            </BarekamText>
          </BarekamButton>
        </div>
      ) : (
        ""
      )}
      {dialogOpen && (
        <LimitAvoidModal
        open={dialogOpen}
        setOpen={setDialogState}
        callBack={addOrder}
      />
      )}

      {/*<OrderPopupCard>*/}
      {/*    <BarekamButton type={OrderButtons.ADD_PRODUCT} onClick={() => onAddNewProduct()}>*/}
      {/*        <BarekamText variant={"xl"} width={'unset'} bold>*/}
      {/*            +*/}
      {/*        </BarekamText>*/}

      {/*        <BarekamText variant={"l"} bold>*/}
      {/*            <Message messageKey={'add_another_product'} sectionId={MessageSections.ORDERS}/>*/}
      {/*        </BarekamText>*/}
      {/*    </BarekamButton>*/}
      {/*</OrderPopupCard>*/}
    </>
  );
};

interface NewOrderPopupProps {
  order: OrderModel;
  orderValidationErrors: { [key: string]: string };
  productValidationErrors: { [key: string]: { [key: string]: string } };
  setOrderValidationErrors: (validations: { [key: string]: string }) => void;
  setProductValidationErrors: (validations: {
    [key: string]: { [key: string]: string };
  }) => void;
  showToast: (success: boolean, withChanges: boolean) => void;
  mainContentContainerRef: MutableRefObject<HTMLElement | null>;
}

export default NewOrderPopup;
